package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import br.com.vvrefeicoes.portal.domain.model.RmpTestAttempt
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Send
import com.composables.icons.lucide.UserPlus
import com.composables.icons.lucide.X
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_decision_cancel
import portalvv.shared.generated.resources.rmp_new_date_placeholder
import portalvv.shared.generated.resources.rmp_test_approved
import portalvv.shared.generated.resources.rmp_test_convocation
import portalvv.shared.generated.resources.rmp_test_convoke
import portalvv.shared.generated.resources.rmp_test_convoke_title
import portalvv.shared.generated.resources.rmp_test_empty
import portalvv.shared.generated.resources.rmp_test_failed
import portalvv.shared.generated.resources.rmp_test_mark_approved
import portalvv.shared.generated.resources.rmp_test_mark_failed
import portalvv.shared.generated.resources.rmp_test_pending
import portalvv.shared.generated.resources.rmp_test_section
import portalvv.shared.generated.resources.rmp_test_send
import portalvv.shared.generated.resources.rmp_test_time_placeholder

/** Campos da convocação (chave da API → rótulo), na ordem da tela do sistema antigo. */
private val CONVOCATION_FIELDS =
    listOf(
        "conv_nome_candidato" to "Nome do candidato *",
        "conv_cpf" to "CPF *",
        "conv_cargo" to "Cargo *",
        "conv_telefone" to "Telefone *",
        "data_convocacao" to "Data do teste *",
        "horario_convocacao" to "Horário *",
        "conv_unidade_teste" to "Unidade de teste *",
        "endereco_convocacao" to "Endereço completo *",
        "conv_pix" to "Chave Pix",
    )

/** Digita o horário só com números: "0830" → "08:30". */
internal fun maskTime(input: String): String {
    val digits = input.filter(Char::isDigit).take(4)
    return if (digits.length > 2) digits.take(2) + ":" + digits.drop(2) else digits
}

/**
 * Teste prático da admissão: os candidatos convocados, o documento de convocação de cada um, a escolha do candidato
 * (gestora da unidade) e a convocação de novos candidatos (RH da unidade).
 */
@Composable
internal fun RmpTestSection(
    approval: RmpApproval,
    attempts: List<RmpTestAttempt>,
    controller: RmpApprovalController,
    onChanged: () -> Unit,
) {
    var convoking by remember(approval.request.id) { mutableStateOf(false) }
    Text(text = stringResource(Res.string.rmp_test_section), style = MaterialTheme.typography.titleMedium)
    if (attempts.isEmpty()) {
        Text(
            text = stringResource(Res.string.rmp_test_empty),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    attempts.forEach { attempt ->
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
            val result =
                when (attempt.result) {
                    "aprovado" -> stringResource(Res.string.rmp_test_approved)
                    "reprovado" -> stringResource(Res.string.rmp_test_failed)
                    else -> stringResource(Res.string.rmp_test_pending)
                }
            Text(
                text = listOf(attempt.data["conv_nome_candidato"].orEmpty(), result).joinToString(" · "),
                style = MaterialTheme.typography.labelLarge,
                color =
                    when (attempt.result) {
                        "aprovado" -> MaterialTheme.colorScheme.primary
                        "reprovado" -> MaterialTheme.colorScheme.error
                        else -> MaterialTheme.colorScheme.onSurface
                    },
            )
            Text(
                text =
                    listOfNotNull(
                        isoToDisplayDate(attempt.data["data_convocacao"].orEmpty()),
                        attempt.data["horario_convocacao"],
                        attempt.data["conv_unidade_teste"],
                    ).joinToString(" · "),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8), verticalAlignment = Alignment.CenterVertically) {
                AppButton(
                    text = stringResource(Res.string.rmp_test_convocation),
                    onClick = { controller.openConvocation(attempt) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.FileText,
                )
                if (approval.canRecordTestResult && attempt.result == "pendente") {
                    AppButton(
                        text = stringResource(Res.string.rmp_test_mark_failed),
                        onClick = { controller.recordTestResult(attempt, "reprovado", onChanged) },
                        enabled = !controller.deciding,
                        variant = PortalButtonVariant.Outlined,
                        leadingIcon = Lucide.X,
                    )
                    AppButton(
                        text = stringResource(Res.string.rmp_test_mark_approved),
                        onClick = { controller.recordTestResult(attempt, "aprovado", onChanged) },
                        enabled = !controller.deciding,
                        variant = PortalButtonVariant.Filled,
                        leadingIcon = Lucide.Check,
                    )
                }
            }
        }
    }
    if (approval.canConvoke) {
        AppButton(
            text = stringResource(Res.string.rmp_test_convoke),
            onClick = { convoking = true },
            enabled = !controller.deciding,
            variant = PortalButtonVariant.Outlined,
            leadingIcon = Lucide.UserPlus,
        )
    }
    if (convoking) {
        RmpConvokeDialog(
            number =
                approval.request.number
                    ?.toString()
                    .orEmpty(),
            cargo =
                approval.request.data["cargo"]
                    ?.let { (it as? JsonPrimitive)?.content }
                    .orEmpty(),
            controller = controller,
            onDismiss = { convoking = false },
            onSaved = {
                convoking = false
                onChanged()
            },
        )
    }
}

/** Convocação de um candidato para o teste prático (RH): os campos do sistema antigo. */
@Composable
private fun RmpConvokeDialog(
    number: String,
    cargo: String,
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    onSaved: () -> Unit,
) {
    var values by remember { mutableStateOf(mapOf("conv_cargo" to cargo)) }
    AppModalCard(title = stringResource(Res.string.rmp_test_convoke_title, number), onDismiss = onDismiss) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        CONVOCATION_FIELDS.forEach { (key, label) ->
            val placeholder =
                when (key) {
                    "data_convocacao" -> stringResource(Res.string.rmp_new_date_placeholder)
                    "horario_convocacao" -> stringResource(Res.string.rmp_test_time_placeholder)
                    else -> null
                }
            AppTextField(
                value = values[key].orEmpty(),
                onValueChange = { input ->
                    val value =
                        when (key) {
                            "data_convocacao" -> maskDate(input)
                            "horario_convocacao" -> maskTime(input)
                            else -> input.take(1000)
                        }
                    values = values + (key to value)
                },
                label = label,
                placeholder = placeholder,
                singleLine = key != "endereco_convocacao",
                modifier = Modifier.fillMaxWidth(),
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.rmp_decision_cancel),
                onClick = onDismiss,
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.rmp_test_send),
                onClick = {
                    val data =
                        buildJsonObject {
                            CONVOCATION_FIELDS.forEach { (key, _) ->
                                val value = values[key].orEmpty()
                                put(key, JsonPrimitive(if (key == "data_convocacao") displayToIsoDate(value) else value))
                            }
                        }
                    controller.convoke(data, onSaved)
                },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Filled,
                leadingIcon = Lucide.Send,
            )
        }
    }
}
